import { FC, useCallback, useEffect, useState } from 'react';

import { getDashboardCoffinOutPacing } from '@shared/api/giftOrder/giftOrder.controller';
import {
  CoffinOutPacingCurvePoint,
  CoffinOutPacingResponse,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import {
  CenterLoading,
  ErrorBox,
  TD_CLASS,
  TH_CLASS,
} from '../../components/DashboardShared';
import { fmt, fmtWonCompact } from './analysisUtils';

/** coffin-out-pacing 미배포 서버는 404 — 장애가 아니라 '미배포'다. */
const isNotFound = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 페이스 기준 축. 발인 기준은 "발인까지 얼마 남았나", 등록 기준은 "부고 등록 후
 * 얼마나 지났나" — 방금 등록된 부고가 많은 코호트엔 등록 기준이 더 공정하다
 * (공유 시각 로그가 없어 등록 시각을 대리 지표로 쓴다).
 */
type PaceAnchor = 'coffinOut' | 'reg';

const ANCHOR_OPTIONS: { key: PaceAnchor; label: string }[] = [
  { key: 'coffinOut', label: '발인 기준' },
  { key: 'reg', label: '등록 기준' },
];

// 런타임엔 axios 인터셉터가 ISO 문자열을 Date로 바꿔놓는다 — new Date()로 흡수 후 KST 날짜만
const isoDayKST = (v: string): string =>
  new Date(new Date(v).getTime() + 9 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

const hoursLabel = (h: number): string => {
  const abs = Math.abs(h);
  const txt = abs >= 48 ? `${Math.round(abs / 24)}일` : `${Math.round(abs)}시간`;
  return h < 0 ? `발인 ${txt} 전` : h === 0 ? '발인' : `발인 ${txt} 후`;
};

interface Props {
  brandId: string;
  teamType: string;
}

/**
 * 판매 페이스 뷰 — "지금 시점에 이 정도 팔린 게 정상인가?"
 *
 * 방금 만든 부고가 많은 코호트(내일·모레 발인)는 화환이 적을 수밖에 없다 —
 * 과거 8주 완료 코호트에서 구매 타이밍 곡선(발인 기준 몇 시간 전에 몇 %가
 * 팔리는가)을 학습해, 진행 중 코호트의 실적을 "지금쯤 팔렸어야 할 양"과
 * 비교한다. 기대치의 분모는 공유 상가 수 — 공유 없인 화환이 팔릴 수 없다.
 */
export const CoffinOutPacing: FC<Props> = function CoffinOutPacing({
  brandId,
  teamType,
}) {
  const [anchor, setAnchor] = useState<PaceAnchor>('coffinOut');
  const [data, setData] = useState<CoffinOutPacingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false); // 구서버 404

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    try {
      setData(
        await getDashboardCoffinOutPacing(
          brandId || undefined,
          teamType || undefined,
        ),
      );
    } catch (e) {
      if (isNotFound(e)) {
        setNotReady(true);
      } else {
        setError(
          e instanceof Error ? e.message : '판매 페이스를 불러올 수 없습니다.',
        );
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [brandId, teamType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) return <CenterLoading />;
  if (notReady) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
        판매 페이스 API가 아직 이 서버에 배포되지 않았습니다. 배포 후 다시
        열어주세요.
      </div>
    );
  }
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return null;

  const { baseline, cohorts, generatedAt } = data;
  // 서버 생성 시각의 KST 날짜 — "오늘" 행 하이라이트용 (인터셉터가 Date로 바꿔도 new Date()가 흡수)
  const todayKey = new Date(
    new Date(generatedAt).getTime() + 9 * 60 * 60 * 1000,
  )
    .toISOString()
    .slice(0, 10);

  return (
    <div className={`space-y-6 ${loading ? 'pointer-events-none opacity-60' : ''}`}>
      {/* 기준(baseline) 요약 */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              판매 페이스 — 지금 시점에 이 정도 팔린 게 정상인가
            </h3>
            <p className="mt-0.5 text-xs text-gray-400">
              최근 8주 완료 코호트(공유 상가만)에서 구매 타이밍을 학습해, 발인
              임박/직후 상가의 실적을 "지금쯤 팔렸어야 할 양"과 비교합니다
            </p>
          </div>
          <div className="flex gap-1">
            {ANCHOR_OPTIONS.map((a) => (
              <button
                key={a.key}
                onClick={() => setAnchor(a.key)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                  anchor === a.key
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
        {anchor === 'reg' && (
          <p className="mt-1 text-xs text-indigo-500">
            등록 기준: 부고 등록 후 경과시간 축 — 방금 등록된 부고가 많은 날에
            더 공정한 비교입니다 (공유 시각 로그가 없어 등록 시각으로 대신)
          </p>
        )}

        {baseline === null ? (
          <p className="mt-4 text-sm text-gray-500">
            학습할 과거 표본이 없습니다 (최근 8주에 공유 상가의 화환 판매 기록
            필요).
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div>
              <p className="text-xs text-gray-500">공유 상가당 화환 (기준)</p>
              <p className="mt-0.5 text-xl font-bold text-gray-900">
                {baseline.avgWreathPerSharedFevent.toFixed(2)}건
              </p>
              <p className="text-xs text-gray-400">
                {fmtWonCompact(baseline.avgWreathAmountPerSharedFevent)}원 ·
                표본 {fmt(baseline.sharedFeventCount)}곳/
                {fmt(baseline.wreathOrderCount)}건
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">구매 타이밍 중앙값</p>
              <p className="mt-0.5 text-xl font-bold text-gray-900">
                {baseline.purchaseLeadHours
                  ? hoursLabel(-baseline.purchaseLeadHours.median)
                  : '—'}
              </p>
              {baseline.purchaseLeadHours && (
                <p className="text-xs text-gray-400">
                  p25~p75: {hoursLabel(-baseline.purchaseLeadHours.p75)} ~{' '}
                  {hoursLabel(-baseline.purchaseLeadHours.p25)}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500">등록 → 화환 구매</p>
              <p className="mt-0.5 text-xl font-bold text-gray-900">
                {baseline.regToPurchaseHours
                  ? `${Math.round(baseline.regToPurchaseHours.median)}시간`
                  : '—'}
              </p>
              {baseline.regToPurchaseHours && (
                <p className="text-xs text-gray-400">
                  p25~p75: {Math.round(baseline.regToPurchaseHours.p25)}~
                  {Math.round(baseline.regToPurchaseHours.p75)}시간
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500">기준 기간</p>
              <p className="mt-0.5 text-sm font-medium text-gray-700">
                {isoDayKST(baseline.from)} ~ {isoDayKST(baseline.to)}
              </p>
              <p className="text-xs text-gray-400">발인 3일 경과 코호트만</p>
            </div>
          </div>
        )}

        {baseline &&
          (anchor === 'coffinOut'
            ? baseline.curve.length > 0 && (
                <PacingCurve curve={baseline.curve} anchor="coffinOut" />
              )
            : baseline.regCurve.length > 0 && (
                <PacingCurve curve={baseline.regCurve} anchor="reg" />
              ))}
      </div>

      {/* 진행 코호트 표 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            발인일별 진행 상황 (D-2 ~ D+3)
          </h3>
          <p className="mt-0.5 text-xs text-gray-400">
            페이스 = 실적 ÷ 지금쯤 팔렸어야 할 양 · 미래 발인일은 아직 등록/공유
            전인 상가가 있어 기대치가 낮게 잡힐 수 있습니다
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={TH_CLASS}>발인일</th>
                <th className={`${TH_CLASS} text-right`}>상가</th>
                <th className={`${TH_CLASS} text-right`}>공유 상가</th>
                <th className={`${TH_CLASS} text-right`}>화환 실적</th>
                <th className={`${TH_CLASS} text-right`}>진행 기대</th>
                <th className={`${TH_CLASS} text-right`}>기대 누적</th>
                <th className={`${TH_CLASS} text-right`}>페이스</th>
                <th className={`${TH_CLASS} text-right`}>예상 최종</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cohorts.map((c) => {
                const d = new Date(`${c.date}T00:00:00+09:00`);
                const wd = WEEKDAY_LABELS[d.getDay()] ?? '';
                const isToday = c.date === todayKey;
                const progress =
                  anchor === 'coffinOut'
                    ? c.expectedProgress
                    : c.expectedProgressReg;
                const soFar =
                  anchor === 'coffinOut'
                    ? c.expectedSoFarCount
                    : c.expectedSoFarCountReg;
                const pace =
                  anchor === 'coffinOut' ? c.paceRatio : c.paceRatioReg;
                const projected =
                  anchor === 'coffinOut'
                    ? c.projectedFinalCount
                    : c.projectedFinalCountReg;
                return (
                  <tr
                    key={c.date}
                    className={`hover:bg-gray-50 ${isToday ? 'bg-indigo-50/50' : ''}`}
                  >
                    <td className={`${TD_CLASS} font-medium`}>
                      {c.date.slice(5).replace('-', '/')} ({wd})
                      {isToday && (
                        <span className="ml-1 rounded bg-indigo-100 px-1 py-0.5 text-[10px] font-bold text-indigo-700">
                          오늘
                        </span>
                      )}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.feventCount > 0 ? `${fmt(c.feventCount)}곳` : '-'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.sharedFeventCount > 0
                        ? `${fmt(c.sharedFeventCount)}곳`
                        : '-'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.wreathCount > 0 ? (
                        <>
                          <span className="font-medium">
                            {fmt(c.wreathCount)}건
                          </span>
                          <span className="block text-xs text-gray-400">
                            {fmtWonCompact(c.wreathAmount)}원
                          </span>
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className={`${TD_CLASS} text-right text-gray-500`}>
                      {progress !== null
                        ? `${(progress * 100).toFixed(0)}%`
                        : '—'}
                    </td>
                    <td className={`${TD_CLASS} text-right text-gray-500`}>
                      {soFar !== null ? `${soFar.toFixed(1)}건` : '—'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      <PaceBadge ratio={pace} />
                    </td>
                    <td className={`${TD_CLASS} text-right text-gray-500`}>
                      {projected !== null ? (
                        <>
                          <span className="font-medium text-gray-700">
                            {fmt(projected)}건
                          </span>
                          <span className="block text-xs text-gray-400">
                            기준 {c.expectedFinalCount?.toFixed(1) ?? '—'}건
                          </span>
                        </>
                      ) : c.expectedFinalCount !== null ? (
                        <span className="text-xs">
                          기준 {c.expectedFinalCount.toFixed(1)}건
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── 내부 컴포넌트 ───────────────────────────────────

/** 페이스 배지 — ≥100% 순항 / 70~100% 보통 / <70% 저조 */
const PaceBadge: FC<{ ratio: number | null }> = ({ ratio }) => {
  if (ratio === null) {
    return <span className="text-xs text-gray-400">집계 전</span>;
  }
  const pct = ratio * 100;
  const tone =
    ratio >= 1
      ? 'bg-emerald-100 text-emerald-700'
      : ratio >= 0.7
        ? 'bg-gray-100 text-gray-600'
        : 'bg-amber-100 text-amber-700';
  const label = ratio >= 1 ? '순항' : ratio >= 0.7 ? '보통' : '저조';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {pct.toFixed(0)}% {label}
    </span>
  );
};

/** 구매 타이밍 누적 곡선 — x: 발인 상대시각 또는 등록 후 경과시간, y: 누적 % */
const PacingCurve: FC<{
  curve: CoffinOutPacingCurvePoint[];
  anchor: PaceAnchor;
}> = ({ curve, anchor }) => {
  const W = 640;
  const H = 200;
  const ML = 40;
  const MB = 28;
  const MT = 10;
  const MR = 14;
  const plotW = W - ML - MR;
  const plotH = H - MT - MB;

  const first = curve[0];
  const last = curve[curve.length - 1];
  if (!first || !last) return null;
  const xMin = first.hours;
  const xMax = last.hours;
  const px = (h: number) => ML + ((h - xMin) / (xMax - xMin)) * plotW;
  const py = (share: number) => MT + plotH - share * plotH;

  const points = curve
    .map((p) => `${px(p.hours)},${py(p.cumShare)}`)
    .join(' ');

  const tickHours =
    anchor === 'coffinOut'
      ? [-96, -48, -24, -12, 0, 24, 72]
      : [0, 12, 24, 48, 72, 96, 120];
  const xTicks = curve.filter((p) => tickHours.includes(p.hours));

  const pointLabel = (p: CoffinOutPacingCurvePoint): string =>
    anchor === 'coffinOut'
      ? `${hoursLabel(p.hours)}: 누적 ${(p.cumShare * 100).toFixed(1)}%`
      : `등록 ${p.hours}시간 후: 누적 ${(p.cumShare * 100).toFixed(1)}%`;

  return (
    <div className="mt-5">
      <p className="mb-1 text-xs font-medium text-gray-600">
        {anchor === 'coffinOut'
          ? '구매 타이밍 누적 곡선 — 발인까지 남은 시간별로 최종 판매량의 몇 %가 팔려 있는가'
          : '구매 타이밍 누적 곡선 — 부고 등록 후 경과시간별로 최종 판매량의 몇 %가 팔려 있는가'}
      </p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        {[0, 0.25, 0.5, 0.75, 1].map((s) => (
          <g key={s}>
            <line
              x1={ML}
              x2={W - MR}
              y1={py(s)}
              y2={py(s)}
              stroke="#f3f4f6"
              strokeWidth={1}
            />
            <text
              x={ML - 5}
              y={py(s)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={9}
              fill="#9ca3af"
            >
              {Math.round(s * 100)}%
            </text>
          </g>
        ))}
        {/* 발인 시점 기준선 (등록 축에선 0h가 왼쪽 끝이라 불필요) */}
        {anchor === 'coffinOut' && (
          <>
            <line
              x1={px(0)}
              x2={px(0)}
              y1={MT}
              y2={MT + plotH}
              stroke="#d1d5db"
              strokeWidth={1}
            />
            <text
              x={px(0)}
              y={MT - 1}
              textAnchor="middle"
              fontSize={9}
              fill="#6b7280"
            >
              발인
            </text>
          </>
        )}
        {xTicks.map((p) => (
          <text
            key={p.hours}
            x={px(p.hours)}
            y={H - MB + 13}
            textAnchor="middle"
            fontSize={9}
            fill="#9ca3af"
          >
            {anchor === 'coffinOut'
              ? p.hours === 0
                ? '0'
                : p.hours < 0
                  ? `${-p.hours}h 전`
                  : `${p.hours}h 후`
              : `${p.hours}h`}
          </text>
        ))}
        <line
          x1={ML}
          x2={W - MR}
          y1={MT + plotH}
          y2={MT + plotH}
          stroke="#d1d5db"
          strokeWidth={1}
        />
        <polyline
          points={points}
          fill="none"
          stroke="#6366f1"
          strokeWidth={2}
        />
        {curve.map((p) => (
          <circle
            key={p.hours}
            cx={px(p.hours)}
            cy={py(p.cumShare)}
            r={3}
            fill="#6366f1"
          >
            <title>{pointLabel(p)}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
};
